"use client";
import React, { useState, useEffect, useRef, useCallback } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { useAuth } from "./context/AuthContext";
import { api } from "../lib/api";
import PaymentMethodCollection from "./PaymentMethodCollection";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from "./ui/dialog";
import { Button } from "./ui/button";
import { useToast } from "../hooks/use-toast";
import InsuranceForm from "./InsuranceForm";
import IntakeForm from "./IntakeForm";

// Currency formatting utilities
const currencyFormatter = new Intl.NumberFormat("en-CA", {
  style: "currency",
  currency: "CAD",
});

const formatCurrency = (value) => {
  const numeric = Number(value);
  return currencyFormatter.format(Number.isFinite(numeric) ? numeric : 0);
};

const roundCurrency = (value) => {
  const numeric = Number(value);
  if (!Number.isFinite(numeric)) {
    return 0;
  }
  return Math.round(numeric * 100) / 100;
};

const sanitizeFilePart = (value) =>
  value
    ?.toString()
    ?.replace(/[^\w\s-]/g, "")
    ?.trim()
    ?.replace(/\s+/g, "_") || "client";

// --- UI atoms
function Avatar({ src, name }) {
  if (src)
    return (
      <img
        src={src}
        alt={name}
        className="h-14 w-14 rounded-full object-cover"
      />
    );
  const initials = (name || "?")
    .split(" ")
    .slice(0, 2)
    .map((s) => s[0]?.toUpperCase())
    .join("");
  return (
    <div className="h-14 w-14 rounded-full bg-gradient-to-br from-emerald-500 to-teal-500 text-white grid place-content-center text-base font-semibold shadow-md">
      {initials}
    </div>
  );
}

export default function Payment() {
  const { state } = useLocation();
  const navigate = useNavigate();
  const { user } = useAuth();
  const { toast } = useToast();

  const selection = state?.selection;
  const context = state?.context || {};
  const bookingData = state?.bookingData || {};
  const referralCodeFromState = state?.referralCode || null; // Extract referral code from navigation state

  // Ensure setting is available - fallback to determining from context or selection
  // If setting is an array, take the first element
  let setting = context?.setting || selection?.setting || null;
  if (Array.isArray(setting)) {
    setting = setting[0] || null;
  }

  const isGuest = !user; // true when no logged-in client

  const [error, setError] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [bookingId, setBookingId] = useState("");
  const [paymentInfo, setPaymentInfo] = useState(null);
  const [paymentOption, setPaymentOption] = useState("payAtClinic"); // "payAtClinic" or "payUpfront"
  const [referralCode, setReferralCode] = useState("");
  const [referralCodeValidating, setReferralCodeValidating] = useState(false);
  const [referralCodeValid, setReferralCodeValid] = useState(null);
  const [referralCodeError, setReferralCodeError] = useState("");
  const [freeMassagesAvailable, setFreeMassagesAvailable] = useState(0);
  const [useFreeMassage, setUseFreeMassage] = useState(false);
  const [loadingRewards, setLoadingRewards] = useState(true);
  const [giftCardCode, setGiftCardCode] = useState("");
  const [giftCardValidating, setGiftCardValidating] = useState(false);
  const [giftCardValid, setGiftCardValid] = useState(null);
  const [giftCardError, setGiftCardError] = useState("");
  const [giftCardAmount, setGiftCardAmount] = useState(0);
  const [giftCardApplied, setGiftCardApplied] = useState(false);
  const [isReceiptOpen, setIsReceiptOpen] = useState(false);
  const [receiptData, setReceiptData] = useState(null);
  const [receiptLoading, setReceiptLoading] = useState(false);
  const [receiptError, setReceiptError] = useState(null);
  const [isGeneratingReceipt, setIsGeneratingReceipt] = useState(false);
  const receiptRef = useRef(null);
  const [logoLoaded, setLogoLoaded] = useState(false);
  const [isIntakeFilled, setIsIntakeFilled] = useState(false);
  const [isInsuranceFilled, setIsInsuranceFilled] = useState(false);
  const [isIntakeModalOpen, setIsIntakeModalOpen] = useState(false);
  const [isInsuranceModalOpen, setIsInsuranceModalOpen] = useState(false);

  // guard: if no booking data or missing required fields, bounce back
  useEffect(() => {
    if (
      !selection?.therapistId ||
      !bookingData?.clientName ||
      !bookingData?.clientEmail ||
      !setting
    ) {
      console.error("Missing required booking data:", {
        therapistId: selection?.therapistId,
        clientName: bookingData?.clientName,
        clientEmail: bookingData?.clientEmail,
        setting: setting,
        contextSetting: context?.setting,
      });
      navigate("/book", { replace: true });
    }
  }, [selection, bookingData, setting, context, navigate]);

  // Fetch user's rewards to check for free massages
  useEffect(() => {
    const fetchRewards = async () => {
      if (!user?.email) {
        setLoadingRewards(false);
        return;
      }
      try {
        setLoadingRewards(true);
        const rewards = await api.rewards.getRewards();
        if (rewards?.success && rewards?.rewards) {
          setFreeMassagesAvailable(rewards.rewards.freeMassagesAvailable || 0);
        }
      } catch (err) {
        console.error("Failed to fetch rewards:", err);
        // Don't show error, just assume no free massages available
        setFreeMassagesAvailable(0);
      } finally {
        setLoadingRewards(false);
      }
    };
    fetchRewards();
  }, [user]);

  // Check for existing forms on mount (Pre-booking check)
  useEffect(() => {
    const checkExistingForms = async () => {
      if (!bookingData?.clientEmail) return;

      try {
        const clientEmail = bookingData.clientEmail;
        
        // Fetch existing insurance and intake records concurrently
        const [insuranceData, intakeData] = await Promise.all([
          api.insurance.get({ email: clientEmail }).catch(() => null),
          api.intake.get({ email: clientEmail }).catch(() => null)
        ]);
        
        if (insuranceData?.insuranceDetails) {
          setIsInsuranceFilled(true);
        }

        // Intake is "Filled" if a form was recently submitted but not yet linked to a booking
        if (intakeData?.intakeForm && !intakeData.intakeForm.bookingId) {
          setIsIntakeFilled(true);
        }
      } catch (error) {
        console.error("Error checking pre-booking forms:", error);
      }
    };

    if (!bookingId) {
      checkExistingForms();
    }
  }, [bookingData?.clientEmail, bookingId]);

  // Handle payment method saved
  function handlePaymentSuccess(payment) {
    console.log("✅ [Payment] Payment success callback received:", payment);
    setPaymentInfo(payment);
    setError("");
  }

  // Calculate actual booking price from service duration and addons
  const servicePrice = context.duration?.price || 0;
  const addOnsPrice = (context.addOns || []).reduce(
    (sum, addOn) => sum + (addOn?.price || 0),
    0,
  );
  const BASE_BOOKING_PRICE = servicePrice + addOnsPrice;

  // Get duration in minutes for validation
  const durationMinutes =
    context.duration?.minutes || context.duration?.duration || 60;

  // Free massage can only be used for 60-minute duration
  const canUseFreeMassage = durationMinutes === 60;

  // If free massage is selected but duration is not 60 minutes, disable it
  useEffect(() => {
    if (useFreeMassage && !canUseFreeMassage) {
      setUseFreeMassage(false);
    }
  }, [durationMinutes, useFreeMassage, canUseFreeMassage]);

  // Calculate discount and adjusted total
  // Free massage only applies to service price, not addons
  const freeMassageDiscount =
    useFreeMassage && canUseFreeMassage ? servicePrice : 0;

  // Gift card discount calculation:
  // - If free massage is used: only apply to addons
  // - If free massage is not used: apply to total (service + addons)
  let giftCardDiscount = 0;
  if (giftCardApplied && giftCardValid === true) {
    if (useFreeMassage && canUseFreeMassage) {
      // When free massage is used, gift card only applies to addons
      giftCardDiscount = Math.min(giftCardAmount, addOnsPrice);
    } else {
      // When free massage is not used, gift card applies to total
      giftCardDiscount = Math.min(giftCardAmount, BASE_BOOKING_PRICE);
    }
  }

  // Calculate subtotal: service (minus free massage if applicable) + addons (always paid)
  const servicePriceAfterFreeMassage = Math.max(
    0,
    servicePrice - freeMassageDiscount,
  );
  const subtotalBeforeGiftCard = servicePriceAfterFreeMassage + addOnsPrice;

  // Apply gift card discount to subtotal
  const subtotalAfterDiscount = Math.max(
    0,
    subtotalBeforeGiftCard - giftCardDiscount,
  );

  // Calculate GST (5%) and service fee
  const SERVICE_FEE = 5.0; // Not charged right now, will be struck through
  const gstAmount = subtotalAfterDiscount * 0.05; // 5% GST
  const adjustedTotal = subtotalAfterDiscount + gstAmount;

  // Booking is fully covered if the adjusted total is 0
  // This can happen when:
  // - Free massage covers service and there are no addons
  // - Gift card covers everything
  // - Free massage covers service and gift card covers addons
  const isFullyCovered = adjustedTotal === 0;

  // Validate gift card code
  async function handleValidateGiftCard() {
    if (!giftCardCode.trim()) {
      setGiftCardValid(null);
      setGiftCardError("");
      setGiftCardAmount(0);
      setGiftCardApplied(false);
      return;
    }

    setGiftCardValidating(true);
    setGiftCardError("");
    setGiftCardValid(null);
    setGiftCardApplied(false);

    try {
      const result = await api.giftCards.validateForBooking(
        giftCardCode.trim().toUpperCase(),
      );
      if (result.isValid && result.amount > 0) {
        setGiftCardValid(true);
        setGiftCardAmount(result.amount);
        setGiftCardApplied(true);
        setGiftCardError("");

        // If gift card fully covers the remaining amount (addons when free massage is used, or total otherwise), clear payment info
        const remainingAmount =
          useFreeMassage && canUseFreeMassage
            ? addOnsPrice
            : BASE_BOOKING_PRICE;
        if (result.amount >= remainingAmount) {
          setPaymentInfo(null);
        }
      } else {
        setGiftCardValid(false);
        setGiftCardAmount(0);
        setGiftCardApplied(false);
        setGiftCardError(result.message || "Invalid or expired code.");
      }
    } catch (err) {
      setGiftCardValid(false);
      setGiftCardAmount(0);
      setGiftCardApplied(false);
      setGiftCardError(err.message || "Invalid or expired code.");
    } finally {
      setGiftCardValidating(false);
    }
  }

  // Handle gift card code change
  function handleGiftCardCodeChange(code) {
    // Format code: uppercase and remove invalid characters
    const formatted = code.toUpperCase().replace(/[^A-Z0-9-]/g, "");
    setGiftCardCode(formatted);
    // Reset validation state when code changes
    if (giftCardValid !== null) {
      setGiftCardValid(null);
      setGiftCardError("");
      setGiftCardAmount(0);
      setGiftCardApplied(false);
    }
  }

  // Validate referral code
  async function handleValidateReferralCode() {
    if (!referralCode.trim()) {
      setReferralCodeValid(null);
      setReferralCodeError("");
      return;
    }

    setReferralCodeValidating(true);
    setReferralCodeError("");
    try {
      const result = await api.referral.validateReferralCode(
        referralCode.trim(),
      );
      if (result.success && result.valid) {
        setReferralCodeValid(true);
        setReferralCodeError("");
      } else {
        setReferralCodeValid(false);
        setReferralCodeError(result.message || "Invalid referral code");
      }
    } catch (err) {
      setReferralCodeValid(false);
      setReferralCodeError(err.message || "Failed to validate referral code");
    } finally {
      setReferralCodeValidating(false);
    }
  }

  // Check if referral code exists in URL (from referral link), navigation state, or localStorage
  useEffect(() => {
    // First check navigation state (from booking flow)
    if (referralCodeFromState) {
      console.log("🎟️ [Payment] Referral code received from navigation");
      setReferralCode(referralCodeFromState.toUpperCase());
      return;
    }

    // Fallback to URL parameter (direct link)
    const urlParams = new URLSearchParams(window.location.search);
    const ref = urlParams.get("ref");
    if (ref) {
      console.log("🎟️ [Payment] Referral code received from URL");
      const code = ref.toUpperCase();
      setReferralCode(code);
      return;
    }

    // Final fallback to localStorage
    const stored = localStorage.getItem("bookatherapy_referralCode");
    if (stored) {
      console.log("🎟️ [Payment] Referral code loaded from storage");
      setReferralCode(stored.toUpperCase());
    }
  }, [referralCodeFromState]);

  // Validate referral code when it changes (debounced)
  useEffect(() => {
    if (!referralCode.trim()) {
      setReferralCodeValid(null);
      setReferralCodeError("");
      return;
    }

    const timeoutId = setTimeout(async () => {
      setReferralCodeValidating(true);
      setReferralCodeError("");
      try {
        const result = await api.referral.validateReferralCode(
          referralCode.trim(),
        );
        if (result.success && result.valid) {
          setReferralCodeValid(true);
          setReferralCodeError("");
        } else {
          setReferralCodeValid(false);
          setReferralCodeError(result.message || "Invalid referral code");
        }
      } catch (err) {
        setReferralCodeValid(false);
        setReferralCodeError(err.message || "Failed to validate referral code");
      } finally {
        setReferralCodeValidating(false);
      }
    }, 500);

    return () => clearTimeout(timeoutId);
  }, [referralCode]);

  // Convert local time to UTC time string (HH:MM format)
  // The backend's slotToISO function treats the time as UTC, so we need to convert
  // local time to UTC before sending. This prevents timezone shift issues.
  const convertLocalTimeToUTC = (localTimeStr, dateISO) => {
    if (!localTimeStr || !dateISO) return null;

    // Parse the local time (HH:MM format)
    const [hours, minutes] = localTimeStr.split(":").map(Number);
    if (isNaN(hours) || isNaN(minutes)) return null;

    // Create a Date object in local timezone for the given date and time
    // Append 'T12:00:00' to avoid timezone issues with date-only strings
    const localDateStr = `${dateISO}T12:00:00`;
    const localDate = new Date(localDateStr);

    // Set the local time (this handles DST automatically)
    localDate.setHours(hours, minutes, 0, 0);

    // Get UTC hours and minutes
    const utcHours = localDate.getUTCHours();
    const utcMinutes = localDate.getUTCMinutes();

    // Return UTC time in HH:MM format
    return `${String(utcHours).padStart(2, "0")}:${String(utcMinutes).padStart(
      2,
      "0",
    )}`;
  };

  const buildAppointmentUtcIsoString = (dateISO, utcTimeStr) => {
    if (!dateISO || !utcTimeStr) return null;
    const [hours, minutes] = utcTimeStr.split(":").map(Number);
    if (Number.isNaN(hours) || Number.isNaN(minutes)) return null;

    const baseDate = new Date(`${dateISO}T00:00:00Z`);
    if (Number.isNaN(baseDate.getTime())) return null;

    baseDate.setUTCHours(hours, minutes, 0, 0);
    return baseDate.toISOString();
  };

  // Normalize time to HH:MM format
  const normalizeTime = (time) => {
    if (!time) return null;

    // If already in HH:MM format, return as is
    if (typeof time === "string" && /^\d{2}:\d{2}$/.test(time)) {
      return time;
    }

    // If it's a Date object or ISO string, extract LOCAL time
    if (
      time instanceof Date ||
      (typeof time === "string" && time.includes("T"))
    ) {
      const date = new Date(time);
      const hours = String(date.getHours()).padStart(2, "0");
      const minutes = String(date.getMinutes()).padStart(2, "0");
      return `${hours}:${minutes}`;
    }

    // Try to parse as time string
    if (typeof time === "string") {
      // Handle formats like "2:30 PM" or "14:30"
      const match = time.match(/(\d{1,2}):(\d{2})\s*(AM|PM)?/i);
      if (match) {
        let hours = parseInt(match[1]);
        const minutes = String(parseInt(match[2])).padStart(2, "0");
        const period = match[3]?.toUpperCase();

        if (period === "PM" && hours !== 12) hours += 12;
        if (period === "AM" && hours === 12) hours = 0;

        return `${String(hours).padStart(2, "0")}:${minutes}`;
      }

      // Try HH:MM format directly
      const parts = time.split(":");
      if (parts.length === 2) {
        const hours = String(parseInt(parts[0])).padStart(2, "0");
        const minutes = String(parseInt(parts[1])).padStart(2, "0");
        return `${hours}:${minutes}`;
      }
    }

    return null;
  };

  // confirm booking
  async function handleConfirm() {
    setError("");

    // Validate free massage can only be used for 60-minute duration
    if (useFreeMassage && !canUseFreeMassage) {
      return setError(
        "Free massages can only be used for 60-minute sessions. Please select a 60-minute duration or add a payment method.",
      );
    }

    // If using free massage but none available, show error
    if (useFreeMassage && freeMassagesAvailable < 1) {
      return setError(
        "No free massages available. Please add a payment method.",
      );
    }

    // Check if payment is required
    // Free massage only covers service price, so if there are addons, payment may still be needed
    const needsPayment = adjustedTotal > 0;

    // If payment is needed but not provided.
    // Exception: guests choosing "pay at clinic" don't need a card — they pay in person.
    const isGuestPayAtClinic = isGuest && paymentOption === "payAtClinic";
    if (needsPayment && !paymentInfo && !isGuestPayAtClinic) {
      return setError(
        "Please add your payment method, use a free massage, or apply a gift card.",
      );
    }

    // For upfront payments, ensure paymentIntentId is present
    if (needsPayment && paymentOption === "payUpfront" && paymentInfo) {
      if (!paymentInfo.paymentIntentId) {
        return setError(
          "Payment was not completed successfully. Please try again.",
        );
      }
      if (!paymentInfo.charged) {
        console.warn(
          "⚠️ [Booking] Upfront payment marked as not charged, but proceeding anyway",
        );
      }
    }

    // Validate date format
    if (
      !bookingData.dateISO ||
      !/^\d{4}-\d{2}-\d{2}$/.test(bookingData.dateISO)
    ) {
      return setError("Please select a valid appointment date.");
    }

    // Normalize time to HH:MM format (local time)
    const normalizedLocalTime = normalizeTime(bookingData.time);
    if (!normalizedLocalTime) {
      return setError("Please select a valid appointment time.");
    }

    // Convert local time to UTC time before sending to backend
    // The backend's slotToISO function treats time as UTC, so we need to convert
    const utcTime = convertLocalTimeToUTC(
      normalizedLocalTime,
      bookingData.dateISO,
    );
    if (!utcTime) {
      return setError("Failed to convert time to UTC. Please try again.");
    }

    const appointmentDateUtcIso = buildAppointmentUtcIsoString(
      bookingData.dateISO,
      utcTime,
    );

    // Validate required fields before creating hold
    if (!setting) {
      return setError(
        "Setting is required. Please go back and select a setting.",
      );
    }
    if (!bookingData.dateISO) {
      return setError("Date is required. Please go back and select a date.");
    }
    if (!utcTime) {
      return setError("Time is required. Please go back and select a time.");
    }

    try {
      setSubmitting(true);
      const hold = await api.createHold({
        therapistId: selection.therapistId,
        setting: setting, // Use the validated setting value
        dateISO: bookingData.dateISO,
        time: utcTime, // Send UTC time to backend
        persona: context.persona || null,
        clientEmail: bookingData.clientEmail,
        clientName: bookingData.clientName,
        clientDob: bookingData.clientDob || null,
        serviceId: context.service?.id,
        serviceName: context.service?.name || context.duration?.name,
        durationMinutes: context.duration?.minutes,
        addOns: context.addOns || [],
        paymentOption, // Let the backend know intent upfront (required for guest pay-at-clinic)
      });

      // Ensure referral code is only sent if valid
      const referralCodeToSend =
        referralCode && referralCode.trim() && referralCodeValid === true
          ? referralCode.trim().toUpperCase()
          : undefined;

      // Ensure gift card code is only sent if valid and applied
      const giftCardCodeToSend =
        giftCardCode &&
        giftCardCode.trim() &&
        giftCardValid === true &&
        giftCardApplied
          ? giftCardCode.trim().toUpperCase()
          : undefined;

      // If using free massage and no addons, or gift card fully covers, pass null paymentInfo
      const paymentInfoToSend =
        (useFreeMassage && canUseFreeMassage && addOnsPrice === 0) ||
        isFullyCovered
          ? null
          : {
              ...paymentInfo,
              paymentOption: paymentOption, // Include payment option
            };

      console.log(
        "📤 [Booking] Confirming hold with paymentInfo:",
        JSON.stringify(paymentInfoToSend, null, 2),
      );
      console.log("📤 [Booking] Payment option:", paymentOption);
      console.log("📤 [Booking] Service price:", servicePrice);
      console.log("📤 [Booking] Addons price:", addOnsPrice);
      console.log("📤 [Booking] Free massage discount:", freeMassageDiscount);
      console.log("📤 [Booking] Gift card discount:", giftCardDiscount);
      console.log("📤 [Booking] GST amount:", gstAmount);
      console.log("📤 [Booking] Total amount (with GST):", adjustedTotal);

      const done = await api.confirmHold({
        holdId: hold.holdId,
        appointmentDateISO: appointmentDateUtcIso, // Send complete ISO string
        paymentInfo: paymentInfoToSend,
        referralCode: referralCodeToSend,
        giftCardCode: giftCardCodeToSend,
        giftCardDiscount: giftCardApplied ? giftCardDiscount : undefined,
        useFreeMassage: useFreeMassage && canUseFreeMassage ? true : undefined, // Only send if valid
        totalAmount: adjustedTotal, // Total amount including GST
        serviceDetails: {
          serviceId: context.service?.id,
          serviceName: context.service?.name || context.duration?.name,
          durationMinutes: context.duration?.minutes,
          addOns: context.addOns || [],
          servicePrice: servicePriceAfterFreeMassage, // Service price after free massage discount
          addOnsPrice: addOnsPrice, // Addons are always paid
          freeMassageDiscount: freeMassageDiscount, // Include free massage discount amount
          gstAmount: gstAmount, // Include GST amount
          gstRate: 0.05, // 5% GST
        },
      });

      if (done?.bookingId && appointmentDateUtcIso) {
        try {
          await api.appointments.updateAppointment(done.bookingId, {
            therapistId: selection.therapistId,
            clientEmail: bookingData.clientEmail,
            appointmentDate: appointmentDateUtcIso,
            duration:
              context.duration?.minutes ||
              context.duration?.duration ||
              context.duration ||
              60,
            status: "pending",
          });
        } catch (updateError) {
          console.warn(
            "⚠️ [Booking] Failed to mark booking as pending:",
            updateError,
          );
        }
      }

      // If using free massage, redeem it after booking is confirmed
      if (useFreeMassage && done.bookingId) {
        try {
          await api.rewards.redeemFreeMassageReward({
            bookingId: done.bookingId,
          });
          // Update available count
          setFreeMassagesAvailable((prev) => Math.max(0, prev - 1));
        } catch (err) {
          console.error("Failed to redeem free massage:", err);
          // Don't fail the booking if redemption fails, just log it
        }
      }

      setBookingId(done.bookingId);

      // Clear referral code from localStorage after successful booking
      localStorage.removeItem("bookatherapy_referralCode");
      // If upfront payment, load and show receipt, then send PDF via email
      if (paymentOption === "payUpfront" && done.bookingId) {
        await loadReceiptData(done.bookingId);
        setIsReceiptOpen(true);
        // Automatically send receipt PDF via email (similar to invoice flow)
        // Wait for the receipt dialog to render before generating PDF
        setTimeout(() => {
          sendReceiptEmailToClient(done.bookingId).catch((error) => {
            console.error("Failed to auto-send receipt email:", error);
            // Non-critical - receipt will be sent when user downloads
          });
        }, 800); // Increased delay to ensure dialog is fully rendered
      }

    } catch (e) {
      setError(e?.message || "Could not complete booking.");
    } finally {
      setSubmitting(false);
    }
  }

  // Preload logo image to ensure it's ready for PDF generation
  useEffect(() => {
    if (isReceiptOpen) {
      const logoImg = new Image();
      logoImg.src = "/logo-black.png";
      logoImg.crossOrigin = "anonymous";
    }
  }, [isReceiptOpen]);

  // Load receipt data
  const loadReceiptData = useCallback(async (bookingId) => {
    if (!bookingId) return;

    setReceiptLoading(true);
    setReceiptError(null);
    try {
      const data = await api.receipt.getBookingReceipt(bookingId);
      setReceiptData(data);
    } catch (error) {
      console.error("Failed to load receipt:", error);
      setReceiptError(error?.message || "Failed to load receipt data");
    } finally {
      setReceiptLoading(false);
    }
  }, []);

  // Helper to wait for images to load
  const waitForImages = useCallback((element) => {
    return new Promise((resolve) => {
      const images = element.querySelectorAll("img");
      if (images.length === 0) {
        resolve();
        return;
      }

      let loadedCount = 0;
      const totalImages = images.length;
      let resolved = false;

      const checkComplete = () => {
        loadedCount++;
        if (loadedCount === totalImages && !resolved) {
          resolved = true;
          resolve();
        }
      };

      // Set a timeout to prevent infinite waiting
      const timeout = setTimeout(() => {
        if (!resolved) {
          resolved = true;
          console.warn("Timeout waiting for images to load, proceeding anyway");
          resolve();
        }
      }, 5000); // 5 second timeout

      images.forEach((img) => {
        if (img.complete && img.naturalHeight !== 0) {
          checkComplete();
        } else {
          const onLoad = () => {
            checkComplete();
            img.removeEventListener("load", onLoad);
            img.removeEventListener("error", onError);
          };
          const onError = () => {
            console.warn("Image failed to load:", img.src);
            checkComplete();
            img.removeEventListener("load", onLoad);
            img.removeEventListener("error", onError);
          };
          img.addEventListener("load", onLoad);
          img.addEventListener("error", onError);
        }
      });

      // Clear timeout if all images load before timeout
      if (loadedCount === totalImages) {
        clearTimeout(timeout);
      }
    });
  }, []);

  // Generate receipt PDF and send via email automatically
  const sendReceiptEmailToClient = useCallback(
    async (bookingId) => {
      try {
        // Ensure receipt data is loaded
        if (!receiptData || receiptLoading) {
          await loadReceiptData(bookingId);
          // Wait a bit for DOM updates
          await new Promise((resolve) => setTimeout(resolve, 100));
        }

        // Wait for receipt dialog to render - retry if needed
        let retries = 0;
        const maxRetries = 10;
        while (!receiptRef.current && retries < maxRetries) {
          await new Promise((resolve) => setTimeout(resolve, 200));
          retries++;
        }

        if (!receiptRef.current) {
          throw new Error(
            "Receipt template is not ready. Receipt will be sent when you download it.",
          );
        }

        // Wait for all images (especially the logo) to load
        await waitForImages(receiptRef.current);
        // Also wait for logo loaded state if available
        if (!logoLoaded) {
          // Wait up to 2 seconds for logo to load
          let logoWaitCount = 0;
          while (!logoLoaded && logoWaitCount < 20) {
            await new Promise((resolve) => setTimeout(resolve, 100));
            logoWaitCount++;
          }
        }
        // Additional delay to ensure rendering is complete and image is visible
        await new Promise((resolve) => setTimeout(resolve, 300));

        const html2canvas = (await import("html2canvas")).default;
        const { jsPDF } = await import("jspdf");

        const canvas = await html2canvas(receiptRef.current, {
          scale: 2,
          useCORS: true,
          backgroundColor: "#ffffff",
          allowTaint: false,
          imageTimeout: 15000, // 15 second timeout for images
        });
        const imageData = canvas.toDataURL("image/png");
        const pdf = new jsPDF({
          orientation: canvas.width > canvas.height ? "landscape" : "portrait",
          unit: "pt",
          format: "a4",
        });
        const pageWidth = pdf.internal.pageSize.getWidth();
        const pageHeight = pdf.internal.pageSize.getHeight();
        const imgProps = pdf.getImageProperties(imageData);
        const ratio = Math.min(
          pageWidth / imgProps.width,
          pageHeight / imgProps.height,
        );
        const imgWidth = imgProps.width * ratio;
        const imgHeight = imgProps.height * ratio;
        const marginX = (pageWidth - imgWidth) / 2;
        const marginY = (pageHeight - imgHeight) / 2;

        pdf.addImage(imageData, "PNG", marginX, marginY, imgWidth, imgHeight);
        const dataUri = pdf.output("datauristring");
        const base64 = dataUri.split(",")[1];

        if (!base64) {
          throw new Error("Failed to prepare receipt PDF for emailing.");
        }

        await api.receipt.emailBookingReceipt(bookingId, base64);

        console.log("✅ Receipt PDF emailed to client automatically");
      } catch (error) {
        console.error("Failed to send receipt email automatically:", error);
        // Don't show error to user - receipt email is non-critical
        // It will be sent when they download the PDF
      }
    },
    [
      receiptData,
      receiptLoading,
      loadReceiptData,
      bookingId,
      waitForImages,
      logoLoaded,
    ],
  );

  // Download receipt as PDF
  const handleDownloadReceipt = useCallback(async () => {
    if (!receiptRef.current || !receiptData || receiptLoading) {
      return;
    }

    setIsGeneratingReceipt(true);
    try {
      // Wait for all images (especially the logo) to load
      await waitForImages(receiptRef.current);
      // Also wait for logo loaded state if available
      if (!logoLoaded) {
        // Wait up to 2 seconds for logo to load
        let logoWaitCount = 0;
        while (!logoLoaded && logoWaitCount < 20) {
          await new Promise((resolve) => setTimeout(resolve, 100));
          logoWaitCount++;
        }
      }
      // Additional delay to ensure rendering is complete and image is visible
      await new Promise((resolve) => setTimeout(resolve, 300));

      const html2canvas = (await import("html2canvas")).default;
      const { jsPDF } = await import("jspdf");

      const canvas = await html2canvas(receiptRef.current, {
        scale: 2,
        useCORS: true,
        backgroundColor: "#ffffff",
        allowTaint: false,
        imageTimeout: 15000, // 15 second timeout for images
      });
      const imageData = canvas.toDataURL("image/png");
      const pdf = new jsPDF({
        orientation: canvas.width > canvas.height ? "landscape" : "portrait",
        unit: "pt",
        format: "a4",
      });
      const pageWidth = pdf.internal.pageSize.getWidth();
      const pageHeight = pdf.internal.pageSize.getHeight();
      const imgProps = pdf.getImageProperties(imageData);
      const ratio = Math.min(
        pageWidth / imgProps.width,
        pageHeight / imgProps.height,
      );
      const imgWidth = imgProps.width * ratio;
      const imgHeight = imgProps.height * ratio;
      const marginX = (pageWidth - imgWidth) / 2;
      const marginY = (pageHeight - imgHeight) / 2;

      pdf.addImage(imageData, "PNG", marginX, marginY, imgWidth, imgHeight);
      const safeClientName = sanitizeFilePart(
        receiptData?.snapshot?.clientName || "client",
      );
      const receiptNumber = receiptData?.receiptNumber || "Receipt";
      pdf.save(`Receipt_${receiptNumber}_${safeClientName}.pdf`);

      // Send receipt via email
      try {
        const dataUri = pdf.output("datauristring");
        const base64 = dataUri.split(",")[1];
        if (base64) {
          await api.receipt.emailBookingReceipt(bookingId, base64);
        }
      } catch (emailError) {
        console.error("Failed to email receipt:", emailError);
        // Don't fail the download if email fails
      }
    } catch (error) {
      console.error("Failed to generate receipt PDF:", error);
    } finally {
      setIsGeneratingReceipt(false);
    }
  }, [receiptData, receiptLoading, bookingId, waitForImages, logoLoaded]);

  // Helper to safely get form data (returns null on 404)
  const safeGetForm = async (getFn) => {
    try {
      const result = await getFn();
      return result;
    } catch (err) {
      // 404 means form doesn't exist, which is fine - return null
      if (err?.status === 404) {
        return null;
      }
      // For other errors, throw to be caught by outer handler
      throw err;
    }
  };

  // Helper to get clientId from token if available
  const getClientIdFromToken = () => {
    try {
      const token = localStorage.getItem("bookatherapy_token");
      if (!token) return null;

      // Decode JWT token to get user ID (sub field)
      const base64Url = token.split(".")[1];
      const base64 = base64Url.replace(/-/g, "+").replace(/_/g, "/");
      const jsonPayload = decodeURIComponent(
        atob(base64)
          .split("")
          .map((c) => {
            return "%" + ("00" + c.charCodeAt(0).toString(16)).slice(-2);
          })
          .join(""),
      );

      const payload = JSON.parse(jsonPayload);
      return payload.sub || payload.id || null;
    } catch (error) {
      console.warn("Could not decode token:", error);
      return null;
    }
  };


  if (!selection?.therapistId) return null;

  // Format booking summary
  const bookingDate = new Date(bookingData.dateISO || "");
  const dateDisplay = bookingDate.toLocaleDateString(undefined, {
    weekday: "long",
    year: "numeric",
    month: "long",
    day: "numeric",
  });

  return (
    <div className="relative min-h-screen bg-slate-100 overflow-x-hidden">
      <div className="pointer-events-none absolute inset-0 overflow-hidden">
        <div className="absolute inset-x-[-10%] top-[-15%] h-[360px] bg-[radial-gradient(circle_at_center,_rgba(12,164,117,0.16),_rgba(148,191,255,0.08)_55%,_transparent_75%)] blur-3xl" />
        <div className="absolute left-[-15%] bottom-[-10%] h-[320px] w-[320px] rounded-full bg-[radial-gradient(circle,_rgba(79,70,229,0.12),_transparent_60%)] blur-3xl" />
        <div className="absolute right-[-10%] top-1/3 h-[280px] w-[280px] rounded-full bg-[radial-gradient(circle,_rgba(6,182,212,0.18),_transparent_55%)] blur-3xl" />
      </div>

      <div className="relative z-10 mx-auto flex w-full max-w-5xl flex-col px-4 pb-16 pt-10 sm:px-6 lg:px-10">
        <header className="mb-10 flex flex-wrap items-center justify-between gap-4 text-slate-700">
          <button
            onClick={() => navigate(-1)}
            className="flex items-center gap-2 rounded-full border border-slate-300/60 bg-white px-4 py-2 text-xs font-semibold uppercase tracking-[0.16em] shadow-sm transition hover:border-emerald-300 hover:text-emerald-700">
            <span className="text-lg">←</span>
            Back to confirm
          </button>
          <div className="flex items-center gap-3 text-[10px] uppercase tracking-[0.26em] text-emerald-600">
            <span className="h-1.5 w-1.5 rounded-full bg-emerald-500 shadow-[0_0_12px_rgba(16,185,129,0.7)]" />
            Secure Payment
          </div>
        </header>

        {!bookingId && (
          <section className="mb-10 text-slate-800">
            <h1 className="text-3xl font-semibold tracking-tight text-slate-900 md:text-4xl">
              Finish securing your booking
            </h1>
            <p className="mt-3 max-w-2xl text-sm text-slate-600">
              Choose how you’d like to pay, apply any rewards or gift cards, and
              add a payment method. You’re one step away from your session.
            </p>

            <div className="mt-6 flex flex-wrap gap-4">
              {[
                { label: "Matches", status: "done" },
                { label: "Review & Confirm", status: "done" },
                { label: "Payment", status: "current" },
              ].map((step, idx) => (
                <div
                  key={step.label}
                  className={`group flex items-center gap-3 rounded-full border px-4 py-2 text-xs font-semibold transition ${
                    step.status === "done"
                      ? "border-emerald-400/40 bg-emerald-400/10 text-emerald-700"
                      : step.status === "current"
                      ? "border-emerald-300 bg-white text-emerald-600 shadow"
                      : "border-slate-200 bg-white text-slate-300"
                  }`}>
                  <span
                    className={`grid h-6 w-6 place-content-center rounded-full text-[10px] ${
                      step.status === "done"
                        ? "bg-emerald-400 text-white"
                        : step.status === "current"
                        ? "bg-emerald-500 text-white"
                        : "border border-slate-200 text-slate-300"
                    }`}>
                    {idx + 1}
                  </span>
                  {step.label}
                </div>
              ))}
            </div>
          </section>
        )}

        {bookingId ? (
          <motion.section
            initial={{ opacity: 0, y: 12 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.4 }}
            className="mx-auto w-full max-w-3xl rounded-3xl border border-emerald-200 bg-white p-8 text-center shadow-[0_30px_80px_-45px_rgba(14,116,144,0.4)]">
            <div className="mx-auto mb-6 flex h-20 w-20 items-center justify-center rounded-full bg-gradient-to-br from-emerald-500 to-teal-500 text-white shadow-lg">
              <span className="text-4xl">✓</span>
            </div>
            <h2 className="text-3xl font-semibold text-slate-900">
              You’re all set!
            </h2>
            <p className="mt-3 text-sm text-slate-600">
              Your booking has been confirmed and a confirmation email is on the
              way.
            </p>

            <div className="mt-8 grid gap-4 rounded-2xl border border-emerald-200 bg-emerald-50/70 p-6 text-left sm:grid-cols-2">
              <div>
                <p className="text-xs uppercase tracking-[0.16em] text-emerald-600">
                  Therapist
                </p>
                <p className="mt-1 font-semibold text-emerald-900">
                  {selection.fullName}
                </p>
              </div>
              <div>
                <p className="text-xs uppercase tracking-[0.16em] text-emerald-600">
                  Date
                </p>
                <p className="mt-1 font-semibold text-emerald-900">
                  {dateDisplay}
                </p>
              </div>
              <div>
                <p className="text-xs uppercase tracking-[0.16em] text-emerald-600">
                  Time
                </p>
                <p className="mt-1 font-semibold text-emerald-900">
                  {bookingData.time}
                </p>
              </div>
              <div>
                <p className="text-xs uppercase tracking-[0.16em] text-emerald-600">
                  Setting
                </p>
                <p className="mt-1 font-semibold text-emerald-900">
                  {setting || context.setting || "Clinic"}
                </p>
              </div>
            </div>

            <div className="mt-6 flex flex-wrap items-center justify-center gap-3 text-sm">
              {paymentInfo?.charged && (
                <span className="inline-flex items-center gap-2 rounded-full bg-blue-100 px-4 py-1.5 font-medium text-blue-700">
                  💳 Payment processed — receipt emailed
                </span>
              )}
              {useFreeMassage && (
                <span className="inline-flex items-center gap-2 rounded-full bg-purple-100 px-4 py-1.5 font-medium text-purple-700">
                  🎁 Free massage redeemed
                </span>
              )}
              {giftCardApplied && giftCardValid === true && (
                <span className="inline-flex items-center gap-2 rounded-full bg-emerald-100 px-4 py-1.5 font-medium text-emerald-700">
                  🎟️ Gift card applied
                </span>
              )}
            </div>


            <button
              onClick={() => navigate("/dashboard")}
              className="mt-8 inline-flex h-12 items-center justify-center rounded-xl bg-gradient-to-r from-emerald-600 to-teal-600 px-8 text-sm font-semibold text-white shadow-lg shadow-emerald-500/30 transition hover:from-emerald-700 hover:to-teal-700">
              Go to Dashboard
            </button>
          </motion.section>
        ) : (
          <div className="grid flex-1 gap-10 lg:grid-cols-[minmax(0,2fr)_minmax(260px,1fr)]">
            <div className="space-y-8">
              <motion.section
                initial={{ opacity: 0, y: 16 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.4, delay: 0.05 }}
                className="w-full rounded-3xl border border-white/70 bg-white p-6 shadow-[0_20px_60px_-40px_rgba(22,163,74,0.45)]">
                <div className="flex flex-col gap-5 sm:flex-row sm:items-center">
                  <Avatar src={selection.photoUrl} name={selection.fullName} />
                  <div className="flex-1 text-slate-800">
                    <div className="flex flex-wrap items-center gap-2">
                      <h2 className="text-xl font-semibold">
                        {selection.fullName}
                      </h2>
                      {context.setting && (
                        <span className="rounded-full border border-emerald-200 bg-emerald-50 px-3 py-1 text-xs font-semibold uppercase tracking-wide text-emerald-600">
                          {context.setting}
                        </span>
                      )}
                    </div>
                    <p className="mt-1 text-sm text-slate-500">
                      {context.service?.name || "Massage Appointment"} •{" "}
                      {context.duration?.duration || 60} min
                    </p>
                    <p className="mt-1 text-xs uppercase tracking-[0.16em] text-emerald-600">
                      {dateDisplay} at {bookingData.time}
                    </p>
                  </div>
                </div>
              </motion.section>

              {freeMassagesAvailable > 0 && (
                <motion.section
                  initial={{ opacity: 0, y: 16 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.4, delay: 0.15 }}
                  className={`w-full rounded-3xl border-2 p-5 ${
                    canUseFreeMassage
                      ? "border-purple-200 bg-gradient-to-r from-purple-50 to-pink-50"
                      : "border-slate-200 bg-slate-50"
                  }`}>
                  <label
                    className={`flex items-start gap-4 ${
                      canUseFreeMassage
                        ? "cursor-pointer"
                        : "cursor-not-allowed"
                    }`}>
                    <input
                      type="checkbox"
                      checked={useFreeMassage && canUseFreeMassage}
                      disabled={!canUseFreeMassage}
                      onChange={(e) => {
                        if (canUseFreeMassage) {
                          setUseFreeMassage(e.target.checked);
                          if (e.target.checked) {
                            // Only clear payment info if there are no addons or gift card doesn't cover addons
                            // Gift card can still be used for addons
                            const remainingAfterGiftCard = Math.max(
                              0,
                              addOnsPrice -
                                (giftCardApplied && giftCardValid === true
                                  ? Math.min(giftCardAmount, addOnsPrice)
                                  : 0),
                            );
                            if (remainingAfterGiftCard === 0) {
                              setPaymentInfo(null);
                            }
                          }
                        }
                      }}
                      className="mt-1 h-5 w-5 text-purple-600 focus:ring-purple-500 rounded disabled:opacity-50 disabled:cursor-not-allowed"
                    />
                    <div className="flex-1">
                      <div className="flex flex-wrap items-center gap-2">
                        <span className="text-xl">🎁</span>
                        <span className="text-base font-semibold text-slate-900">
                          Use a free massage reward
                        </span>
                        <span className="rounded-full bg-purple-100 px-2 py-1 text-xs font-semibold text-purple-700">
                          {freeMassagesAvailable} available
                        </span>
                      </div>
                      <p className="mt-2 text-sm text-slate-600">
                        {canUseFreeMassage ? (
                          <>
                            Select this option to redeem one of your free
                            sessions.
                            <span className="block mt-1 font-medium text-purple-700">
                              Note: Free massage applies to service fee only.
                              Add-ons must be paid separately.
                            </span>
                          </>
                        ) : (
                          <span className="text-red-600 font-medium">
                            Free massages can only be used for 60-minute
                            sessions. Your current booking is {durationMinutes}{" "}
                            minutes.
                          </span>
                        )}
                      </p>
                    </div>
                  </label>
                </motion.section>
              )}

              <motion.section
                initial={{ opacity: 0, y: 16 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.4, delay: 0.18 }}
                className="w-full rounded-3xl border border-white/70 bg-white p-6">
                <h3 className="text-lg font-semibold text-slate-900">
                  Required Documentation
                </h3>
                <p className="mt-1 text-sm text-slate-500">
                  Please complete these forms to ensure your therapist has everything they need for your session.
                </p>

                <div className="mt-5 grid grid-cols-1 gap-4 sm:grid-cols-2">
                  {/* Insurance Button */}
                  <button
                    onClick={() => setIsInsuranceModalOpen(true)}
                    className={`flex items-center justify-between gap-3 rounded-2xl border-2 p-4 transition text-left ${
                      isInsuranceFilled ? "border-emerald-100 bg-emerald-50/50" : "border-slate-100 bg-slate-50/50 hover:border-slate-200"
                    }`}
                  >
                    <div className="flex-1">
                      <p className="text-sm font-semibold text-slate-900">Insurance Details</p>
                      <p className="text-xs text-slate-500 mt-0.5">
                        {isInsuranceFilled ? "✓ Details on file" : "Recommended for billing"}
                      </p>
                    </div>
                    <div className={`h-6 w-6 rounded-full flex items-center justify-center ${isInsuranceFilled ? "bg-emerald-500 text-white" : "bg-slate-200 text-slate-400"}`}>
                      {isInsuranceFilled ? "✓" : "+"}
                    </div>
                  </button>

                  {/* Intake Form Button */}
                  <button
                    onClick={() => setIsIntakeModalOpen(true)}
                    className={`flex items-center justify-between gap-3 rounded-2xl border-2 p-4 transition text-left ${
                      isIntakeFilled ? "border-emerald-100 bg-emerald-50/50" : "border-emerald-200 bg-emerald-50/30 hover:border-emerald-300 animate-pulse-subtle"
                    }`}
                  >
                    <div className="flex-1">
                      <p className="text-sm font-semibold text-slate-900">Intake Form</p>
                      <p className="text-xs text-slate-500 mt-0.5">
                        {isIntakeFilled ? "✓ Completed" : "Required for each session"}
                      </p>
                    </div>
                    <div className={`h-6 w-6 rounded-full flex items-center justify-center ${isIntakeFilled ? "bg-emerald-500 text-white" : "bg-emerald-200 text-emerald-600"}`}>
                      {isIntakeFilled ? "✓" : "!"}
                    </div>
                  </button>
                </div>
              </motion.section>

              {!useFreeMassage && (
                <motion.section
                  initial={{ opacity: 0, y: 16 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.4, delay: 0.2 }}
                  className="w-full rounded-3xl border border-white/70 bg-white p-6">
                  <h3 className="text-lg font-semibold text-slate-900">
                    Select payment option
                  </h3>
                  <p className="mt-1 text-sm text-slate-500">
                    {isGuest
                      ? "Book now and pay at the clinic — no account required."
                      : "Your card is stored securely. Choose how you'd like to settle after your session."}
                  </p>

                  <div className="mt-4 space-y-3">
                    <label
                      className={`flex items-start gap-4 rounded-2xl border-2 p-4 transition ${
                        paymentOption === "payAtClinic"
                          ? "border-emerald-500 bg-emerald-50"
                          : "border-slate-200 bg-white hover:border-slate-300"
                      }`}>
                      <input
                        type="radio"
                        name="paymentOption"
                        value="payAtClinic"
                        checked={paymentOption === "payAtClinic"}
                        onChange={(e) => setPaymentOption(e.target.value)}
                        className="mt-1 h-4 w-4 text-emerald-600 focus:ring-emerald-500"
                      />
                      <div className="flex-1">
                        <div className="flex flex-wrap items-center gap-2">
                          <span className="font-semibold text-slate-900">
                            Pay at clinic
                          </span>
                          <span className="rounded-full bg-emerald-100 px-2 py-1 text-xs font-semibold text-emerald-700">
                            Most popular
                          </span>
                          {isGuest && (
                            <span className="rounded-full bg-teal-100 px-2 py-1 text-xs font-semibold text-teal-700">
                              No account needed
                            </span>
                          )}
                        </div>
                        <p className="mt-1 text-sm text-slate-600">
                          {isGuest
                            ? "Book instantly without signing in. Pay in person at the clinic."
                            : "Keep your card on file for convenience and no-shows. You'll pay in person after your appointment."}
                        </p>
                      </div>
                    </label>

                    <label
                      className={`flex items-start gap-4 rounded-2xl border-2 p-4 transition ${
                        isGuest
                          ? "border-slate-200 bg-slate-50 opacity-60 cursor-not-allowed"
                          : paymentOption === "payUpfront"
                          ? "border-emerald-500 bg-emerald-50"
                          : "border-slate-200 bg-white hover:border-slate-300"
                      }`}>
                      <input
                        type="radio"
                        name="paymentOption"
                        value="payUpfront"
                        checked={paymentOption === "payUpfront"}
                        onChange={(e) => !isGuest && setPaymentOption(e.target.value)}
                        disabled={isGuest}
                        className="mt-1 h-4 w-4 text-emerald-600 focus:ring-emerald-500 disabled:cursor-not-allowed"
                      />
                      <div className="flex-1">
                        <div className="flex flex-wrap items-center gap-2">
                          <span className="font-semibold text-slate-900">
                            Pay upfront
                          </span>
                          {isGuest ? (
                            <span className="rounded-full bg-slate-100 px-2 py-1 text-xs font-semibold text-slate-500">
                              Sign in required
                            </span>
                          ) : (
                            <span className="rounded-full bg-blue-100 px-2 py-1 text-xs font-semibold text-blue-700">
                              Instant receipt
                            </span>
                          )}
                        </div>
                        <p className="mt-1 text-sm text-slate-600">
                          {isGuest
                            ? "Create an account or sign in to pay upfront and get an instant receipt."
                            : "Complete payment now and receive your insurance-ready receipt immediately after booking."}
                        </p>
                      </div>
                    </label>
                  </div>
                </motion.section>
              )}

              {useFreeMassage && canUseFreeMassage && addOnsPrice > 0 && (
                <motion.section
                  initial={{ opacity: 0, y: 16 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.4, delay: 0.2 }}
                  className="w-full rounded-3xl border border-white/70 bg-white p-6">
                  <h3 className="text-lg font-semibold text-slate-900">
                    Select payment option for add-ons
                  </h3>
                  <p className="mt-1 text-sm text-slate-500">
                    Your service is covered by your free massage. Add a payment
                    method for add-ons or use a gift card.
                  </p>

                  <div className="mt-4 space-y-3">
                    <label
                      className={`flex items-start gap-4 rounded-2xl border-2 p-4 transition ${
                        paymentOption === "payAtClinic"
                          ? "border-emerald-500 bg-emerald-50"
                          : "border-slate-200 bg-white hover:border-slate-300"
                      }`}>
                      <input
                        type="radio"
                        name="paymentOption"
                        value="payAtClinic"
                        checked={paymentOption === "payAtClinic"}
                        onChange={(e) => setPaymentOption(e.target.value)}
                        className="mt-1 h-4 w-4 text-emerald-600 focus:ring-emerald-500"
                      />
                      <div className="flex-1">
                        <div className="flex flex-wrap items-center gap-2">
                          <span className="font-semibold text-slate-900">
                            Pay at clinic
                          </span>
                          <span className="rounded-full bg-emerald-100 px-2 py-1 text-xs font-semibold text-emerald-700">
                            Most popular
                          </span>
                        </div>
                        <p className="mt-1 text-sm text-slate-600">
                          Keep your card on file for convenience and no-shows.
                          You'll pay in person after your appointment.
                        </p>
                      </div>
                    </label>

                    <label
                      className={`flex items-start gap-4 rounded-2xl border-2 p-4 transition ${
                        isGuest
                          ? "border-slate-200 bg-slate-50 opacity-60 cursor-not-allowed"
                          : paymentOption === "payUpfront"
                          ? "border-emerald-500 bg-emerald-50"
                          : "border-slate-200 bg-white hover:border-slate-300"
                      }`}>
                      <input
                        type="radio"
                        name="paymentOption"
                        value="payUpfront"
                        checked={paymentOption === "payUpfront"}
                        onChange={(e) => !isGuest && setPaymentOption(e.target.value)}
                        disabled={isGuest}
                        className="mt-1 h-4 w-4 text-emerald-600 focus:ring-emerald-500 disabled:cursor-not-allowed"
                      />
                      <div className="flex-1">
                        <div className="flex flex-wrap items-center gap-2">
                          <span className="font-semibold text-slate-900">
                            Pay upfront
                          </span>
                          {isGuest ? (
                            <span className="rounded-full bg-slate-100 px-2 py-1 text-xs font-semibold text-slate-500">
                              Sign in required
                            </span>
                          ) : (
                            <span className="rounded-full bg-blue-100 px-2 py-1 text-xs font-semibold text-blue-700">
                              Instant receipt
                            </span>
                          )}
                        </div>
                        <p className="mt-1 text-sm text-slate-600">
                          {isGuest
                            ? "Create an account or sign in to pay upfront and get an instant receipt."
                            : "Complete payment now and receive your insurance-ready receipt immediately after booking."}
                        </p>
                      </div>
                    </label>
                  </div>
                </motion.section>
              )}

              <motion.section
                initial={{ opacity: 0, y: 16 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.4, delay: 0.25 }}
                className="w-full space-y-6 rounded-3xl border border-white/70 bg-white p-6">
                <div>
                  <label className="block text-sm font-semibold text-slate-800">
                    Referral code
                  </label>
                  <p className="text-xs text-slate-500">
                    Share the love—earn rewards for you and your friend.
                  </p>
                  <div className="mt-3 flex flex-col gap-2 sm:flex-row">
                    <input
                      type="text"
                      value={referralCode}
                      onChange={(e) => {
                        setReferralCode(
                          e.target.value
                            .toUpperCase()
                            .replace(/[^A-Z0-9]/g, ""),
                        );
                        setReferralCodeValid(null);
                        setReferralCodeError("");
                      }}
                      maxLength={8}
                      placeholder="FRIEND20"
                      className={`flex-1 rounded-xl border-2 px-4 py-3 transition ${
                        referralCodeValid === true
                          ? "border-emerald-500 bg-emerald-50"
                          : referralCodeValid === false
                          ? "border-red-300 bg-red-50"
                          : "border-slate-200 bg-white"
                      } focus:outline-none focus:ring-2 focus:ring-emerald-500 focus:border-transparent`}
                    />
                    <div className="flex items-center gap-2">
                      {referralCodeValidating && (
                        <div className="h-5 w-5 animate-spin rounded-full border-2 border-emerald-500 border-t-transparent" />
                      )}
                      {referralCodeValid === true && (
                        <span className="text-lg text-emerald-600">✓</span>
                      )}
                    </div>
                  </div>
                  {referralCodeError && (
                    <p className="mt-2 text-sm text-red-600">
                      {referralCodeError}
                    </p>
                  )}
                  {referralCodeValid === true && (
                    <p className="mt-2 text-sm text-emerald-600">
                      Referral applied! Rewards will be added after your
                      session.
                    </p>
                  )}
                </div>

                <div>
                  <label className="block text-sm font-semibold text-slate-800">
                    Gift card or promo code
                  </label>
                  <p className="text-xs text-slate-500">
                    {useFreeMassage && canUseFreeMassage
                      ? "Apply a gift card to cover add-ons. Your service is already covered by your free massage."
                      : "Apply a BookaTherapy gift card or promo code."}
                  </p>
                  <div className="mt-3 flex flex-col gap-2 sm:flex-row">
                    <input
                      type="text"
                      value={giftCardCode}
                      onChange={(e) => handleGiftCardCodeChange(e.target.value)}
                      placeholder="GIFT-2025"
                      className={`flex-1 rounded-xl border-2 px-4 py-3 transition ${
                        giftCardValid === true
                          ? "border-emerald-500 bg-emerald-50"
                          : giftCardValid === false
                          ? "border-red-300 bg-red-50"
                          : "border-slate-200 bg-white"
                      } focus:outline-none focus:ring-2 focus:ring-emerald-500 focus:border-transparent`}
                    />
                    <button
                      type="button"
                      onClick={() => {
                        handleValidateGiftCard();
                      }}
                      disabled={giftCardValidating || !giftCardCode.trim()}
                      className="inline-flex items-center justify-center rounded-xl bg-gradient-to-r from-emerald-600 to-teal-600 px-6 py-3 text-sm font-semibold text-white shadow-md shadow-emerald-500/30 transition hover:from-emerald-700 hover:to-teal-700 disabled:cursor-not-allowed disabled:opacity-60">
                      {giftCardValidating ? (
                        <div className="h-5 w-5 animate-spin rounded-full border-2 border-white border-t-transparent" />
                      ) : (
                        "Redeem"
                      )}
                    </button>
                    {giftCardValid === true && (
                      <span className="flex items-center text-lg text-emerald-600">
                        ✓
                      </span>
                    )}
                  </div>
                  {giftCardError && (
                    <p className="mt-2 text-sm text-red-600">{giftCardError}</p>
                  )}
                  {giftCardValid === true && (
                    <div className="mt-3 rounded-xl border border-emerald-200 bg-emerald-50 p-4 text-sm text-emerald-700">
                      Gift card applied —{" "}
                      {isFullyCovered
                        ? useFreeMassage && canUseFreeMassage
                          ? "your add-ons are fully covered."
                          : "your booking is fully covered."
                        : `remaining total: $${adjustedTotal.toFixed(2)}`}
                    </div>
                  )}
                </div>
              </motion.section>

              {!isFullyCovered && (
                <motion.section
                  initial={{ opacity: 0, y: 16 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.4, delay: 0.3 }}
                  className="w-full rounded-3xl border border-white/70 bg-white p-6">
                  <h3 className="text-lg font-semibold text-slate-900">
                    {isGuest && paymentOption === "payAtClinic" ? "Confirm booking" : "Payment method"}
                  </h3>
                  <p className="mt-1 text-sm text-slate-500">
                    {isGuest && paymentOption === "payAtClinic"
                      ? "Your appointment will be reserved. You'll pay in person at the clinic."
                      : useFreeMassage && canUseFreeMassage
                      ? "Add a payment method for add-ons. We use bank-level encryption and never store full card details."
                      : "We use bank-level encryption and never store full card details."}
                  </p>
                  <div className="mt-4">
                    {isGuest && paymentOption === "payAtClinic" ? (
                      // Guest pay-at-clinic: no card needed, just confirm
                      <button
                        onClick={handleConfirm}
                        disabled={submitting || !isIntakeFilled}
                        className="inline-flex h-12 w-full items-center justify-center gap-2 rounded-xl bg-gradient-to-r from-emerald-600 to-teal-600 text-sm font-semibold text-white shadow-lg shadow-emerald-500/30 transition hover:from-emerald-700 hover:to-teal-700 disabled:cursor-not-allowed disabled:opacity-60">
                        {submitting ? (
                          <>
                            <div className="h-5 w-5 animate-spin rounded-full border-2 border-white border-t-transparent" />
                            <span>Confirming…</span>
                          </>
                        ) : (
                          "Confirm Booking"
                        )}
                      </button>
                    ) : (
                      <PaymentMethodCollection
                        clientEmail={bookingData.clientEmail}
                        clientName={bookingData.clientName}
                        paymentOption={paymentOption}
                        onSuccess={handlePaymentSuccess}
                        onError={setError}
                        onConfirmBooking={handleConfirm}
                        submitting={submitting}
                        disabled={!isIntakeFilled}
                        adjustedAmount={
                          adjustedTotal > 0 ? adjustedTotal * 100 : undefined
                        }
                      />
                    )}
                  </div>
                </motion.section>
              )}

              {/* Only show this section if fully covered OR free massage with no addons (adjustedTotal === 0) */}
              {/* If free massage is used but adjustedTotal > 0, the payment section above will handle the confirm button */}
              {(isFullyCovered ||
                (useFreeMassage &&
                  canUseFreeMassage &&
                  adjustedTotal === 0)) && (
                <motion.div
                  initial={{ opacity: 0, y: 10 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.4, delay: 0.35 }}
                  className="w-full rounded-3xl border border-white/70 bg-white p-6">
                  <h3 className="text-lg font-semibold text-slate-900">
                    Confirm booking
                  </h3>
                  <p className="mt-2 text-sm text-slate-600">
                    {useFreeMassage && canUseFreeMassage
                      ? giftCardApplied &&
                        giftCardValid === true &&
                        isFullyCovered
                        ? "Your free massage covers the service, and your gift card covers the add-ons. No payment method is required."
                        : "Your free massage will be applied automatically."
                      : "Your gift card covers the full amount, so no payment method is required."}
                  </p>
                  <p className="mt-2 text-center text-[10px] leading-relaxed text-slate-400">
                    {!isIntakeFilled 
                      ? "Please fill out the intake form to enable booking."
                      : paymentOption === "payAtClinic"
                        ? useFreeMassage 
                          ? "Your free massage will be applied automatically."
                          : "Pay in person after your session. No charge will be made today."
                        : "Your card will be charged the total amount upon confirmation."}
                  </p>
                  <button
                    onClick={handleConfirm}
                    disabled={submitting || !isIntakeFilled}
                    className="mt-4 inline-flex h-12 w-full items-center justify-center gap-2 rounded-xl bg-gradient-to-r from-emerald-600 to-teal-600 text-sm font-semibold text-white shadow-lg shadow-emerald-500/30 transition hover:from-emerald-700 hover:to-teal-700 disabled:cursor-not-allowed disabled:opacity-60">
                    {submitting ? (
                      <>
                        <div className="h-5 w-5 animate-spin rounded-full border-2 border-white border-t-transparent" />
                        <span>Confirming…</span>
                      </>
                    ) : (
                      <>
                        <span className="text-lg">✓</span>
                        <span>Confirm booking</span>
                      </>
                    )}
                  </button>
                </motion.div>
              )}

              {error && (
                <motion.div
                  initial={{ opacity: 0 }}
                  animate={{ opacity: 1 }}
                  className="rounded-2xl border border-red-200 bg-red-50 p-4 text-sm text-red-700">
                  <div className="flex items-start gap-2">
                    <span className="text-lg">⚠️</span>
                    <p className="flex-1">{error}</p>
                  </div>
                </motion.div>
              )}
            </div>

            <motion.aside
              initial={{ opacity: 0, y: 16 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.4, delay: 0.25 }}
              className="w-full min-w-0 space-y-6 lg:w-auto">
              <div className="space-y-6 lg:sticky lg:top-10">
                <section className="w-full rounded-3xl border border-white/70 bg-white p-6 text-slate-800 shadow-[0_20px_65px_-35px_rgba(34,211,238,0.35)]">
                  <h3 className="text-lg font-semibold">Booking summary</h3>
                  <p className="mt-1 text-xs text-slate-500">
                    Review the details before finalising.
                  </p>
                  <dl className="mt-5 space-y-3 text-sm">
                    <div className="flex justify-between gap-3">
                      <dt className="text-slate-500">Therapist</dt>
                      <dd className="font-medium text-slate-800 text-right">
                        {selection.fullName}
                      </dd>
                    </div>
                    <div className="flex justify-between gap-3">
                      <dt className="text-slate-500">Setting</dt>
                      <dd className="font-medium text-slate-800 text-right">
                        {context.setting || setting || "Clinic"}
                      </dd>
                    </div>
                    <div className="flex justify-between gap-3">
                      <dt className="text-slate-500">Date</dt>
                      <dd className="font-medium text-slate-800 text-right">
                        {dateDisplay}
                      </dd>
                    </div>
                    <div className="flex justify-between gap-3">
                      <dt className="text-slate-500">Time</dt>
                      <dd className="font-medium text-slate-800 text-right">
                        {bookingData.time}
                      </dd>
                    </div>
                  </dl>

                  <div className="mt-6 space-y-2 rounded-2xl bg-slate-50 p-4 text-sm">
                    <div className="flex justify-between text-slate-600">
                      <span>Service ({durationMinutes} min)</span>
                      <span className="font-medium text-slate-900">
                        ${servicePrice.toFixed(2)}
                      </span>
                    </div>
                    {addOnsPrice > 0 && (
                      <div className="flex justify-between text-slate-600">
                        <span>Add-ons</span>
                        <span className="font-medium text-slate-900">
                          ${addOnsPrice.toFixed(2)}
                        </span>
                      </div>
                    )}
                    {freeMassageDiscount > 0 && (
                      <div className="flex justify-between text-purple-600">
                        <span>Free massage (service only)</span>
                        <span className="font-medium">
                          -${freeMassageDiscount.toFixed(2)}
                        </span>
                      </div>
                    )}
                    {giftCardDiscount > 0 && (
                      <div className="flex justify-between text-emerald-600">
                        <span>Gift card</span>
                        <span className="font-medium">
                          -${giftCardDiscount.toFixed(2)}
                        </span>
                      </div>
                    )}
                    <div className="flex justify-between text-slate-400">
                      <span className="line-through">Service fee</span>
                      <span className="font-medium line-through">
                        ${SERVICE_FEE.toFixed(2)}
                      </span>
                    </div>
                    {subtotalAfterDiscount > 0 && (
                      <div className="flex justify-between text-slate-600">
                        <span>GST (5%)</span>
                        <span className="font-medium text-slate-900">
                          ${gstAmount.toFixed(2)}
                        </span>
                      </div>
                    )}
                    <div className="flex items-center justify-between border-t border-slate-200 pt-2 text-base font-semibold text-slate-900">
                      <span>Today's total</span>
                      <span>${adjustedTotal.toFixed(2)}</span>
                    </div>
                  </div>

                  <div className="mt-4 rounded-2xl border border-slate-200 bg-slate-50 px-4 py-3 text-xs text-slate-500">
                    Secure checkout • encrypted by Stripe • cancel up to 2 hours
                    before your session.
                  </div>
                </section>

                <section className="w-full rounded-3xl border border-white/70 bg-white p-5 text-xs text-slate-600">
                  <h4 className="text-sm font-semibold text-slate-800">
                    Need a hand?
                  </h4>
                  <p className="mt-2 leading-relaxed">
                    Our care team is available 7 days a week by chat or email to
                    help with payment questions, receipts, or insurance
                    paperwork.
                  </p>
                </section>
              </div>
            </motion.aside>
          </div>
        )}
      </div>

      {/* Receipt Dialog */}
      <Dialog
        open={isReceiptOpen}
        onOpenChange={(open) => {
          setIsReceiptOpen(open);
          if (!open) {
            setReceiptError(null);
            setIsGeneratingReceipt(false);
            setReceiptLoading(false);
            setLogoLoaded(false); // Reset logo loaded state when dialog closes
          }
        }}>
        <DialogContent className="max-w-3xl max-h-[90vh] overflow-auto">
          <DialogHeader>
            <DialogTitle>Receipt</DialogTitle>
            <DialogDescription>
              Your payment receipt is ready for download and has been emailed to
              you.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            {receiptError && (
              <div className="rounded-lg border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-700">
                {receiptError}
              </div>
            )}
            <div className="rounded-2xl border border-palette-smoke/50 bg-white shadow-sm">
              {receiptLoading ? (
                <div className="flex items-center justify-center py-12">
                  <p className="text-palette-space">Loading receipt...</p>
                </div>
              ) : receiptData ? (
                <div
                  ref={receiptRef}
                  className="rounded-2xl bg-white p-8 text-slate-900">
                  <div className="flex flex-wrap items-start justify-between gap-6">
                    <div className="space-y-2">
                      <img
                        src="/logo-black.png"
                        alt="Book a Therapy"
                        width={160}
                        height={48}
                        className="h-12 w-auto object-contain"
                        crossOrigin="anonymous"
                        loading="eager"
                        onLoad={() => setLogoLoaded(true)}
                        onError={() => {
                          console.warn("Logo failed to load");
                          setLogoLoaded(true); // Set to true anyway to not block PDF generation
                        }}
                      />
                      <p className="text-sm text-slate-600">
                        {receiptData.snapshot?.therapistClinic || ""}
                      </p>
                    </div>
                    <div className="space-y-2 text-right">
                      <h2 className="text-2xl font-semibold text-slate-900">
                        Receipt
                      </h2>
                      <p className="text-sm text-slate-600">
                        Receipt #: {receiptData.receiptNumber || "Receipt"}
                      </p>
                      <p className="text-sm text-slate-600">
                        Issued:{" "}
                        {receiptData.issuedAt
                          ? new Date(receiptData.issuedAt).toLocaleDateString()
                          : "--"}
                      </p>
                      <p className="text-sm text-slate-600">
                        Appointment:{" "}
                        {receiptData.appointmentDate
                          ? new Date(
                              receiptData.appointmentDate,
                            ).toLocaleDateString()
                          : "--"}
                        {bookingData.time && ` • ${bookingData.time}`}
                      </p>
                      <p className="text-sm text-slate-600">
                        Payment method: {receiptData.paymentMethod || "Card"}
                      </p>
                    </div>
                  </div>

                  <div className="mt-8 rounded-lg border border-slate-200 p-6">
                    <h3 className="text-xs font-semibold uppercase tracking-wide text-slate-500">
                      Client
                    </h3>
                    <dl className="mt-3 space-y-2 text-sm text-slate-700">
                      <div className="flex justify-between gap-4">
                        <dt className="text-slate-600">Name</dt>
                        <dd className="font-medium text-slate-900">
                          {receiptData.snapshot?.clientName ||
                            bookingData.clientName ||
                            "N/A"}
                        </dd>
                      </div>
                      <div className="flex justify-between gap-4">
                        <dt className="text-slate-600">Email</dt>
                        <dd className="font-medium text-slate-900">
                          {receiptData.snapshot?.clientEmail ||
                            bookingData.clientEmail ||
                            "N/A"}
                        </dd>
                      </div>
                      {receiptData.snapshot?.clientDob && (
                        <div className="flex justify-between gap-4">
                          <dt className="text-slate-600">DOB</dt>
                          <dd className="font-medium text-slate-900">
                            {new Date(
                              receiptData.snapshot.clientDob,
                            ).toLocaleDateString()}
                          </dd>
                        </div>
                      )}
                    </dl>
                  </div>

                  <div className="mt-6 overflow-hidden rounded-lg border border-slate-200">
                    <div className="bg-slate-50 px-6 py-3 text-sm font-semibold text-slate-700">
                      Appointment details
                    </div>
                    <div className="space-y-4 px-6 py-5 text-sm text-slate-700">
                      <div className="flex justify-between gap-4">
                        <span className="text-slate-600">Service</span>
                        <span className="font-medium text-slate-900">
                          {receiptData.service?.name ||
                            context.service?.name ||
                            "Therapy session"}
                        </span>
                      </div>
                      <div className="flex justify-between gap-4">
                        <span className="text-slate-600">Duration</span>
                        <span className="font-medium text-slate-900">
                          {receiptData.service?.durationMinutes
                            ? `${receiptData.service.durationMinutes} min`
                            : "N/A"}
                        </span>
                      </div>
                      <div className="flex justify-between gap-4">
                        <span className="text-slate-600">Add-ons</span>
                        <span className="font-medium text-slate-900">
                          {receiptData.service?.addOns?.length
                            ? receiptData.service.addOns.join(", ")
                            : "None"}
                        </span>
                      </div>
                    </div>
                  </div>

                  <div className="mt-6 grid gap-6 rounded-lg border border-slate-200 p-6 md:grid-cols-2">
                    <div className="space-y-2 text-sm text-slate-700">
                      <div className="flex justify-between gap-4">
                        <span>Service amount</span>
                        <span className="font-medium text-slate-900">
                          {formatCurrency(
                            roundCurrency(
                              receiptData.totals?.serviceAmount ?? 0,
                            ),
                          )}
                        </span>
                      </div>
                      <div className="flex justify-between gap-4">
                        <span>Add-on amount</span>
                        <span className="font-medium text-slate-900">
                          {formatCurrency(
                            roundCurrency(receiptData.totals?.addonAmount ?? 0),
                          )}
                        </span>
                      </div>
                      <div className="flex justify-between gap-4">
                        <span>Subtotal</span>
                        <span className="font-medium text-slate-900">
                          {formatCurrency(
                            roundCurrency(receiptData.totals?.subtotal ?? 0),
                          )}
                        </span>
                      </div>
                      <div className="flex justify-between gap-4">
                        <span>Service tax (5%)</span>
                        <span className="font-medium text-slate-900">
                          {formatCurrency(
                            roundCurrency(receiptData.totals?.serviceTax ?? 0),
                          )}
                        </span>
                      </div>
                      <div className="flex justify-between gap-4">
                        <span>Service fee ( not included )</span>
                        <span className="font-medium text-slate-400">
                          {formatCurrency(
                            roundCurrency(receiptData.totals?.serviceFee ?? 0),
                          )}
                        </span>
                      </div>
                      {receiptData.totals?.tipAmount > 0 && (
                        <div className="flex justify-between gap-4">
                          <span>Tip</span>
                          <span className="font-medium text-slate-900">
                            {formatCurrency(
                              roundCurrency(receiptData.totals.tipAmount),
                            )}
                          </span>
                        </div>
                      )}
                      <div className="mt-3 flex justify-between gap-4 border-t border-slate-200 pt-3 text-base font-semibold text-slate-900">
                        <span>
                          Total (subtotal + tax
                          {receiptData.totals?.tipAmount > 0 ? " + tip" : ""})
                        </span>
                        <span>
                          {formatCurrency(
                            roundCurrency(
                              (receiptData.totals?.subtotal ?? 0) +
                                (receiptData.totals?.serviceTax ?? 0) +
                                (receiptData.totals?.tipAmount ?? 0),
                            ),
                          )}
                        </span>
                      </div>
                    </div>
                    <div className="space-y-4 text-sm text-slate-700">
                      <div>
                        <p className="font-semibold text-slate-900">
                          Payment Method
                        </p>
                        <p>{receiptData.paymentMethod || "Card"}</p>
                      </div>
                      <div>
                        <p className="font-semibold text-slate-900">
                          Service Tax
                        </p>
                        <p>Applied at 5% of the service subtotal.</p>
                      </div>
                      <div className="rounded-lg bg-slate-50 p-4 text-sm text-slate-600">
                        This is an auto-generated receipt, you will get the
                        actual invoice once the appointment is confirmed and
                        complete.
                      </div>
                    </div>
                  </div>
                </div>
              ) : (
                <div className="flex items-center justify-center py-12">
                  <p className="text-palette-space">
                    Receipt data is not available.
                  </p>
                </div>
              )}
            </div>
          </div>
          <DialogFooter>
            <Button variant="ghost" onClick={() => setIsReceiptOpen(false)}>
              Close
            </Button>
            <Button
              onClick={handleDownloadReceipt}
              disabled={isGeneratingReceipt || receiptLoading || !receiptData}>
              {isGeneratingReceipt ? "Generating..." : "Download PDF"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Insurance Dialog */}
      <Dialog open={isInsuranceModalOpen} onOpenChange={setIsInsuranceModalOpen}>
        <DialogContent className="max-w-4xl max-h-[90vh] overflow-y-auto p-6 md:p-10">
          <InsuranceForm 
            clientEmail={bookingData.clientEmail}
            clientName={bookingData.clientName}
            clientDob={bookingData.clientDob}
            onComplete={() => {
              setIsInsuranceFilled(true);
              setIsInsuranceModalOpen(false);
              toast({
                title: "Insurance details saved",
                description: "Your information has been successfully stored.",
              });
            }}
          />
        </DialogContent>
      </Dialog>

      {/* Intake Form Dialog */}
      <Dialog open={isIntakeModalOpen} onOpenChange={setIsIntakeModalOpen}>
        <DialogContent className="max-w-4xl max-h-[90vh] overflow-y-auto p-6 md:p-10 border border-slate-100">
          <IntakeForm 
            clientEmail={bookingData.clientEmail}
            clientName={bookingData.clientName}
            onComplete={() => {
              setIsIntakeFilled(true);
              setIsIntakeModalOpen(false);
              toast({
                title: "Intake form completed",
                description: "Thank you for providing your information.",
              });
            }}
          />
        </DialogContent>
      </Dialog>
    </div>
  );
}
